import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useQuery } from '@sequencehq/api/utils'
import { MINUTES } from '@sequencehq/utils/dates'

/**
 * Xero integration status for the Push to Xero feature.
 *
 * - `hasConnection` gates whether the "Push to Xero" action is shown at all: the
 *   account has connected the Xero integration (`state === 'CONNECTED'`). This
 *   is deliberately NOT the `integration.enabled` flag — that flag is the
 *   customer-import workflow toggle (see xero.loader.ts), unrelated to whether
 *   Xero is set up on the account.
 * - `isConnected` is the stricter check used inside the modal: the connection
 *   is live and its auth has not lapsed. A connected-but-auth-lapsed account
 *   still sees the button, but the modal warns and blocks the push until the
 *   connection is restored.
 */
export const useXeroIntegrationStatus = (params?: {
  enabled?: boolean
}): {
  hasConnection: boolean
  isConnected: boolean
  isLoading: boolean
} => {
  const query = useQuery(dashboard20240730Client.getIntegration, 'Xero', {
    staleTime: 1 * MINUTES,
    enabled: params?.enabled ?? true,
    select: integration => ({
      hasConnection: integration?.state === 'CONNECTED',
      isConnected:
        integration?.state === 'CONNECTED' && !integration?.authFailure
    })
  })

  const { hasConnection, isConnected } = query.data ?? {}

  return {
    hasConnection: hasConnection ?? false,
    isConnected: isConnected ?? false,
    isLoading: query.isPending
  }
}
